import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { AuthPage, AuthSplit } from '@quizzach/styles';
import { LoginForm, Navigation, RegistrationForm } from '@quizzach/components';
import { IcCheck } from '@quizzach/assets';
import { RoutePath } from '@quizzach/interfaces';
import { loadGoogleScript, scrollToTop } from '@quizzach/utils';
import { useWindowListener } from '@quizzach/contexts';

const stagePerks = ['perkCollection', 'perkGratis', 'perkCart'] as const;

const GoogleG = () => (
	<svg width='18' height='18' viewBox='0 0 18 18' aria-hidden>
		<path
			fill='#4285F4'
			d='M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62z'
		/>
		<path
			fill='#34A853'
			d='M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z'
		/>
		<path
			fill='#FBBC05'
			d='M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z'
		/>
		<path
			fill='#EA4335'
			d='M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z'
		/>
	</svg>
);

/** One view for /login and /register: the stage sells the account, the
 *  form column switches between the two actions via tabs. */
export const Auth = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { width } = useWindowListener();

	const isRegister = location.pathname === RoutePath.Register;
	const { google_referer } = location.state || {};

	const [buttonWidth, setButtonWidth] = useState(width - 40);

	// The GIS button renders at its own fixed size inside an iframe; once
	// it exists, stretch it over the visual pill so their boxes coincide.
	const gwrapRef = useRef<HTMLDivElement>(null);
	const [gisScale, setGisScale] = useState('');

	useEffect(() => {
		loadGoogleScript();
		scrollToTop();
	}, []);

	useEffect(() => {
		let timer: number;
		let tries = 0;
		// layout offset of the GIS button inside .gis (offset chain is
		// transform-independent, so re-runs measure the same values)
		const offsetWithin = (el: HTMLElement, root: HTMLElement) => {
			let x = 0;
			let y = 0;
			let node: HTMLElement | null = el;
			while (node && node !== root) {
				x += node.offsetLeft;
				y += node.offsetTop;
				node = node.offsetParent as HTMLElement | null;
			}
			return { x, y };
		};
		const fit = () => {
			const wrap = gwrapRef.current;
			const gis = wrap?.querySelector<HTMLElement>('.gis');
			const pill = wrap?.querySelector<HTMLElement>('.gbtn');
			// GIS renders the clickable button as div[role=button]; the iframe
			// is a hidden helper (kept as fallback for older renderings)
			const target =
				gis?.querySelector<HTMLElement>('div[role=button]') ||
				gis?.querySelector<HTMLElement>('iframe');
			if (gis && pill && target && target.offsetWidth > 0) {
				const sx = pill.offsetWidth / target.offsetWidth;
				const sy = pill.offsetHeight / target.offsetHeight;
				const { x, y } = offsetWithin(target, gis);
				setGisScale(
					`translate(${-x * sx}px, ${-y * sy}px) scale(${sx}, ${sy})`
				);
				return;
			}
			if (tries++ < 80) timer = window.setTimeout(fit, 125);
		};
		fit();
		return () => window.clearTimeout(timer);
	}, [width]);

	useEffect(() => {
		if (google_referer) {
			sessionStorage.setItem('referer', google_referer);
		}
	}, [google_referer]);

	useEffect(() => {
		if (width <= 440) {
			setButtonWidth(width - 40);
		} else setButtonWidth(400);
	}, [width]);

	// Tab switch navigates between the two routes (the remount re-runs the
	// GIS script scan, same as today's cross-page links); replace keeps the
	// history clean and the location state travels along.
	const switchTo = (path: RoutePath) => {
		if (location.pathname !== path) {
			navigate(path, { state: location.state, replace: true });
		}
	};

	return (
		<AuthPage data-id='auth-container'>
			<Navigation />

			<AuthSplit>
				<div className='stage'>
					<span className='wmk' aria-hidden>
						{t('auth.stage.watermark')}
					</span>
					<span className='hand'>{t('auth.stage.hand')}</span>
					<h1>
						{t('auth.stage.titleLead')} <em>{t('auth.stage.titleAccent')}</em>
					</h1>
					<ul className='vlist'>
						{stagePerks.map((perk) => (
							<li key={perk}>
								<span className='tick'>
									<IcCheck size={13} />
								</span>
								{t(`auth.stage.${perk}`)}
							</li>
						))}
					</ul>
				</div>

				<div className='formcol'>
					<div className='inner'>
						<div className='atabs' role='tablist'>
							<button
								type='button'
								role='tab'
								aria-selected={!isRegister}
								className={!isRegister ? 'on' : ''}
								onClick={() => switchTo(RoutePath.Login)}
							>
								{t('auth.tabs.login')}
							</button>
							<button
								type='button'
								role='tab'
								aria-selected={isRegister}
								className={isRegister ? 'on' : ''}
								onClick={() => switchTo(RoutePath.Register)}
							>
								{t('auth.tabs.register')}
							</button>
						</div>

						<div className='gwrap' ref={gwrapRef}>
							<span className='gbtn' aria-hidden>
								<GoogleG />
								{t('auth.googleButton')}
							</span>
							<div
								className='gis'
								style={gisScale ? { transform: gisScale } : undefined}
							>
								<div
									id='g_id_onload'
									data-client_id='262018644453-8h0kcatg85os3bd3qaeik1e62jbiovuf.apps.googleusercontent.com'
									data-context='use'
									data-ux_mode='redirect'
									data-login_uri='https://quizzach.com/google-auth'
									data-auto_prompt='false'
								></div>

								<div
									className='g_id_signin'
									data-type='standard'
									data-shape='pill'
									data-theme='outline'
									data-text='continue_with'
									data-size='large'
									data-locale='hr'
									data-logo_alignment='left'
									data-width={buttonWidth}
								></div>
							</div>
						</div>

						<div className='or'>
							<span>{t('auth.common.or')}</span>
						</div>

						{isRegister ? <RegistrationForm /> : <LoginForm />}
					</div>
				</div>
			</AuthSplit>
		</AuthPage>
	);
};
